import { useEffect, useState, type FormEvent } from "react";
import { User, Mail, Save, ShieldCheck, Key, AlertCircle } from "lucide-react";
import { fetchJsonCached, invalidateApiCache } from "../../app/api/cache";
import { useAuth } from "../../app/providers/auth-provider";
import { createAuthClient } from "../auth/api/auth-client";

interface UserProfile {
  name: string;
  email: string;
  bio: string;
  jobTitle: string;
}

interface AdminUser {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  isAdmin: boolean;
}

export function SettingsView() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile>({
    name: "",
    email: "",
    bio: "",
    jobTitle: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UserProfile>(profile);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersStatus, setUsersStatus] = useState("");

  const handleSave = () => {
    setProfile(editForm);
    setIsEditing(false);
  };

  const loadUsers = async () => {
    setUsersStatus("Loading users…");
    try {
      setUsers(await fetchJsonCached<AdminUser[]>("/api/admin/users"));
      setUsersStatus("");
    } catch {
      setUsersStatus("Unable to load users");
    }
  };

  useEffect(() => {
    if (user?.isAdmin) {
      loadUsers();
    }
  }, [user?.isAdmin]);

  const updateUser = async (target: AdminUser, updates: Partial<AdminUser>) => {
    setUsers((prev) =>
      prev.map((item) =>
        item.id === target.id ? { ...item, ...updates } : item,
      ),
    );
    const res = await fetch(`/api/admin/users/${target.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });

    if (!res.ok) {
      invalidateApiCache("/api/admin/users");
      await loadUsers();
      setUsersStatus("Update failed");
      return;
    }

    invalidateApiCache("/api/admin/users");
    const updated: AdminUser = await res.json();
    setUsers((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item)),
    );
    setUsersStatus("");
  };

  return (
    <div className="p-md sm:p-margin-page w-full max-w-[800px] mx-auto overflow-auto overflow-x-hidden h-full">
      <div className="mb-xl">
        <h2 className="font-h1 text-primary tracking-tight font-bold mb-xs">
          Settings
        </h2>
        <p className="font-body-md text-secondary">
          Manage your account and preferences.
        </p>
      </div>

      <div className="bg-surface-container-lowest border border-outline-variant rounded-lg p-lg">
        <div className="flex items-center justify-between mb-lg border-b border-outline-variant pb-md">
          <h3 className="font-h3 text-primary flex items-center gap-2">
            <User size={20} className="text-primary" />
            User Profile
          </h3>
          {!isEditing && (
            <button
              onClick={() => {
                setEditForm(profile);
                setIsEditing(true);
              }}
              className="px-4 py-2 bg-surface-container-low hover:bg-surface-container-high border border-outline-variant rounded text-primary font-label-md transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>

        {isEditing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex flex-col gap-md"
          >
            <div>
              <label className="block font-label-sm text-secondary mb-xs">
                Full Name
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, name: e.target.value }))
                }
                className="w-full bg-surface-container-lowest border border-outline-variant rounded px-3 py-2 font-body-md text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
              />
            </div>
            <div>
              <label className="block font-label-sm text-secondary mb-xs">
                Job Title
              </label>
              <input
                type="text"
                value={editForm.jobTitle}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, jobTitle: e.target.value }))
                }
                className="w-full bg-surface-container-lowest border border-outline-variant rounded px-3 py-2 font-body-md text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
              />
            </div>
            <div>
              <label className="block font-label-sm text-secondary mb-xs">
                Email Address
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-outline"
                />
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="w-full bg-surface-container-lowest border border-outline-variant rounded pl-9 pr-3 py-2 font-body-md text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block font-label-sm text-secondary mb-xs">
                Bio
              </label>
              <textarea
                value={editForm.bio}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, bio: e.target.value }))
                }
                className="w-full bg-surface-container-lowest border border-outline-variant rounded px-3 py-2 font-body-md text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors min-h-[100px] resize-y"
              />
            </div>
            <div className="flex items-center gap-3 mt-sm pt-md border-t border-outline-variant">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditForm(profile);
                }}
                className="px-4 py-2 hover:bg-surface-variant rounded text-secondary font-label-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded hover:opacity-90 font-label-md transition-opacity"
              >
                <Save size={16} /> Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col sm:flex-row gap-xl items-start">
            <div className="w-24 h-24 rounded-full bg-surface-container border border-outline-variant flex flex-col items-center justify-center text-secondary shrink-0">
              <User size={40} className="mb-1" />
              <span className="font-mono-sm text-[10px] uppercase">Avatar</span>
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-y-lg gap-x-md">
              <div>
                <p className="font-label-sm text-secondary mb-1">Full Name</p>
                <p className="font-body-lg text-primary font-medium">
                  {profile.name || "—"}
                </p>
              </div>
              <div>
                <p className="font-label-sm text-secondary mb-1">Job Title</p>
                <p className="font-body-md text-primary">
                  {profile.jobTitle || "—"}
                </p>
              </div>
              <div>
                <p className="font-label-sm text-secondary mb-1">
                  Email Address
                </p>
                <div className="flex items-center gap-2 font-body-md text-primary">
                  <Mail size={16} className="text-secondary" />
                  {profile.email || "—"}
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="font-label-sm text-secondary mb-1">Bio</p>
                <p className="font-body-md text-primary leading-relaxed">
                  {profile.bio || "—"}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Email Change ── */}
      <ChangeEmailSection />

      {/* ── Password Change ── */}
      <ChangePasswordSection />

      {user?.isAdmin && (
        <div className="bg-surface-container-lowest border border-outline-variant rounded-lg p-lg mt-lg">
          <div className="flex items-center justify-between mb-lg border-b border-outline-variant pb-md">
            <h3 className="font-h3 text-primary flex items-center gap-2">
              <ShieldCheck size={20} className="text-primary" />
              User Access
            </h3>
            {usersStatus && (
              <span className="font-label-sm text-secondary">
                {usersStatus}
              </span>
            )}
          </div>

          <div className="overflow-x-hidden">
            <table className="w-full table-fixed text-left">
              <thead>
                <tr className="border-b border-outline-variant">
                  <th className="py-2 pr-4 font-label-sm text-secondary uppercase tracking-wider">
                    User
                  </th>
                  <th className="py-2 px-4 font-label-sm text-secondary uppercase tracking-wider">
                    Enabled
                  </th>
                  <th className="py-2 pl-4 font-label-sm text-secondary uppercase tracking-wider">
                    Admin
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant">
                {users.map((managedUser) => {
                  const isCurrentUser = managedUser.id === user.id;

                  return (
                    <tr key={managedUser.id}>
                      <td className="py-3 pr-4">
                        <p className="font-body-md text-primary font-medium break-words">
                          {managedUser.name}
                        </p>
                        <p className="font-mono-sm text-secondary break-all">
                          {managedUser.email}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <label className="inline-flex items-center gap-2 font-body-md text-primary">
                          <input
                            type="checkbox"
                            checked={managedUser.enabled}
                            disabled={isCurrentUser}
                            onChange={(e) =>
                              updateUser(managedUser, {
                                enabled: e.target.checked,
                              })
                            }
                            className="h-4 w-4 accent-primary disabled:cursor-not-allowed"
                          />
                          {managedUser.enabled ? "Enabled" : "Disabled"}
                        </label>
                      </td>
                      <td className="py-3 pl-4">
                        <label className="inline-flex items-center gap-2 font-body-md text-primary">
                          <input
                            type="checkbox"
                            checked={managedUser.isAdmin}
                            disabled={isCurrentUser}
                            onChange={(e) =>
                              updateUser(managedUser, {
                                isAdmin: e.target.checked,
                              })
                            }
                            className="h-4 w-4 accent-primary disabled:cursor-not-allowed"
                          />
                          {managedUser.isAdmin ? "Admin" : "Member"}
                        </label>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && !usersStatus && (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-6 text-center font-body-md text-secondary"
                    >
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Email Change ─────────────────────────────────────────────

function ChangeEmailSection() {
  const { user, refreshSession } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChangeEmail = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!newEmail) return;
    setIsLoading(true);

    try {
      const authClient = createAuthClient();
      const res = await authClient.changeEmail({
        newEmail,
      });
      if (res.error) {
        setError(res.error.message ?? "Failed to change email");
      } else {
        setMessage("Email updated successfully.");
        setNewEmail("");
        await refreshSession();
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-surface-container-lowest border border-outline-variant rounded-lg p-lg mt-lg">
      <div className="flex items-center gap-2 mb-lg border-b border-outline-variant pb-md">
        <Mail size={20} className="text-primary" />
        <h3 className="font-h3 text-primary">Change Email</h3>
      </div>

      <form onSubmit={handleChangeEmail} className="flex flex-col gap-md">
        <div>
          <label className="block font-label-sm text-secondary mb-xs">
            Current Email
          </label>
          <p className="font-body-md text-primary">{user?.email || "—"}</p>
        </div>
        <div>
          <label
            className="block font-label-sm text-secondary mb-xs"
            htmlFor="new-email"
          >
            New Email
          </label>
          <input
            id="new-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="w-full max-w-[400px] bg-surface-container-lowest border border-outline-variant rounded px-3 py-2 font-body-md text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            placeholder="new@example.com"
            required
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 font-body-md text-error bg-error-container rounded px-3 py-2 max-w-[400px]">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        {message && (
          <div className="font-body-md text-primary bg-surface-container-high rounded px-3 py-2 max-w-[400px]">
            {message}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !newEmail}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded hover:opacity-90 font-label-md transition-opacity disabled:opacity-50 disabled:cursor-not-allowed w-fit"
        >
          <Save size={16} />
          {isLoading ? "Updating…" : "Change Email"}
        </button>
      </form>
    </div>
  );
}

// ── Password Change ──────────────────────────────────────────

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }

    setIsLoading(true);

    try {
      const authClient = createAuthClient();
      const res = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: false,
      });
      if (res.error) {
        setError(res.error.message ?? "Failed to change password");
      } else {
        setMessage("Password changed successfully.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-surface-container-lowest border border-outline-variant rounded-lg p-lg mt-lg">
      <div className="flex items-center gap-2 mb-lg border-b border-outline-variant pb-md">
        <Key size={20} className="text-primary" />
        <h3 className="font-h3 text-primary">Change Password</h3>
      </div>

      <form onSubmit={handleChangePassword} className="flex flex-col gap-md">
        <div>
          <label
            className="block font-label-sm text-secondary mb-xs"
            htmlFor="current-password"
          >
            Current Password
          </label>
          <input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full max-w-[400px] bg-surface-container-lowest border border-outline-variant rounded px-3 py-2 font-body-md text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            placeholder="Enter current password"
            required
            autoComplete="current-password"
          />
        </div>
        <div>
          <label
            className="block font-label-sm text-secondary mb-xs"
            htmlFor="new-password"
          >
            New Password
          </label>
          <input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full max-w-[400px] bg-surface-container-lowest border border-outline-variant rounded px-3 py-2 font-body-md text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            placeholder="At least 8 characters"
            required
            autoComplete="new-password"
            minLength={8}
          />
        </div>
        <div>
          <label
            className="block font-label-sm text-secondary mb-xs"
            htmlFor="confirm-password"
          >
            Confirm New Password
          </label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full max-w-[400px] bg-surface-container-lowest border border-outline-variant rounded px-3 py-2 font-body-md text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            placeholder="Re-enter new password"
            required
            autoComplete="new-password"
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 font-body-md text-error bg-error-container rounded px-3 py-2 max-w-[400px]">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        {message && (
          <div className="font-body-md text-primary bg-surface-container-high rounded px-3 py-2 max-w-[400px]">
            {message}
          </div>
        )}

        <button
          type="submit"
          disabled={
            isLoading || !currentPassword || !newPassword || !confirmPassword
          }
          className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded hover:opacity-90 font-label-md transition-opacity disabled:opacity-50 disabled:cursor-not-allowed w-fit"
        >
          <Key size={16} />
          {isLoading ? "Changing…" : "Change Password"}
        </button>
      </form>
    </div>
  );
}
